import { LitElement, html } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';
import { dates, Button } from '@omegagrid/core';
import { msg, getLocale } from '@omegagrid/localize';
import rp from '@reporting/rp-app-host';
import { Accounting, AccountingRights } from '@reporting/rp-types/core';
import { MODULE_COLORS, MODULE_CODES } from '@reporting/rp-types/accounting';
import { style } from './updatesInfo.style';

const formatDateTime = (value: string) => dates.getDateFormatter(getLocale(), dates.getDateFormat({ withTime: true }))(value);

const remarkIconMap: Record<string, string> = {
	'INFO': 'circle-info',
	'WARNING': 'circle-exclamation',
	'SUCCESS': 'circle-check',
	'DANGER': 'circle-x'
};

const remarkRowStyleMap: Record<string, string> = {
	'INFO': '',
	'WARNING': 'background-color: var(--og-colortype-yellow-background-color); color: var(--og-colortype-yellow-text-color);',
	'SUCCESS': 'background-color: var(--og-colortype-green-background-color); color: var(--og-colortype-green-text-color);',
	'DANGER': 'background-color: var(--og-colortype-red-background-color); color: var(--og-colortype-red-text-color);'
};

@customElement('rp-app-updatesinfo')
export class UpdatesInfo extends LitElement {

	static styles = [style];

	@state()
	accounting: Accounting;

	@query('#refreshButton')
	refreshButton: Button;

	async connectedCallback() {
		super.connectedCallback();
		if (this.hasAccess()) {
			this.accounting = rp.accountingProvider.accounting;
		} // no access = no data = no component
	}

	hasAccess(right: AccountingRights | null = null) {
		if (right !== null)  return rp.userProvider.hasRight(right);
		return rp.userProvider.hasRight(AccountingRights.Finance)
			|| rp.userProvider.hasRight(AccountingRights.Items)
			|| rp.userProvider.hasRight(AccountingRights.Aging);
	}

	updated() {
		this.dispatchEvent(new CustomEvent('resizestop'));
	}

	async refresh() {
		this.refreshButton.disabled = true;
		this.accounting = await rp.ms('accounting').get<Accounting>('');
		this.refreshButton.disabled = false;
		this.dispatchEvent(new CustomEvent('refresh'));
	}

	render = () => this.accounting ? html`
		<og-panel collapsible expanded>
			<div class="header" slot="header">
				<div class="updates-info">
					<og-icon class="updates-icon" icon="hourglass-half"></og-icon>
					<div class="updates-title">${msg('Data update times')}</div>
				</div>
			</div>
			<div class="body" slot="body">
					<table>
						${this.accounting.fin_flag && this.hasAccess(AccountingRights.Finance) ? html`
							<tr>
								<td>
									<og-icon
										icon="cube"
										color="${MODULE_COLORS.get(MODULE_CODES.FINANCE)}">
									</og-icon>
									 ${msg(this.accounting.fin_module_name || 'Finance')}
								</td>
								<td>${this.accounting.fin_updated ? formatDateTime(this.accounting.fin_updated) : '-'}</td>
							</tr>
							${this.accounting.fin_remark ? html`
								<tr style="${remarkRowStyleMap[this.accounting.fin_remark_type]}">
									<td colspan="2">
										<og-icon icon="${remarkIconMap[this.accounting.fin_remark_type] || 'circle-question'}"> </og-icon>
										${this.accounting.fin_remark || '-'}
									</td>
								</tr>
							` : null}
						` : null}
						${this.accounting.item_flag && this.hasAccess(AccountingRights.Items) ? html`
							<tr style="border-top: 3px solid var(--og-border-color);">
								<td>
									<og-icon
										icon="cube"
										color="${MODULE_COLORS.get(MODULE_CODES.ITEMS)}">
									</og-icon>
									 ${msg(this.accounting.item_module_name || 'Items')}
								</td>
								<td>${this.accounting.item_updated ? formatDateTime(this.accounting.item_updated) : '-'}</td>
							</tr>
							${this.accounting.item_remark ? html`
								<tr style="${remarkRowStyleMap[this.accounting.item_remark_type]}">
									<td colspan="2">
										<og-icon icon="${remarkIconMap[this.accounting.item_remark_type] || 'circle-question'}"></og-icon>
										${this.accounting.item_remark || '-'}
									</td>
								</tr>
							` : null}
						` : null}
						${this.accounting.balance_flag && this.hasAccess(AccountingRights.Aging) ? html`
							<tr style="border-top: 3px solid var(--og-border-color);">
								<td>
									<og-icon
										icon="cube"
										color="${MODULE_COLORS.get(MODULE_CODES.AGING)}">
									</og-icon>
									 ${msg(this.accounting.balance_module_name || 'Aging')}
								</td>
								<td>${this.accounting.balance_updated ? formatDateTime(this.accounting.balance_updated) : '-'}</td>
							</tr>
							${this.accounting.balance_remark ? html`
								<tr style="${remarkRowStyleMap[this.accounting.balance_remark_type]}">
									<td colspan="2">
										<og-icon icon="${remarkIconMap[this.accounting.balance_remark_type] || 'circle-question'}"></og-icon>
										${this.accounting.balance_remark || '-'}
									</td>
								</tr>
							` : null}
						` : null}
						<tr>
							<td colspan="2">
								<div class="updates-buttons">
									<og-button
										id="refreshButton"
										icon="arrow-rotate-right"
										style="flex: 1;"
										.text="${msg('Refresh')}"
										@click="${(e: PointerEvent) => {
											e.stopImmediatePropagation();
											this.refresh();
										}}">
									</og-button>
								</div>
							</td>
						</tr>
					</table>
			</div>
		</og-panel>
	` : null;

}
